import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LoginError, useAuth } from "../auth/AuthContext";

function HeroLogo({ size = 48 }: { size?: number }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <rect
        x="2"
        y="2"
        width="28"
        height="28"
        rx="6"
        fill="var(--paper-2)"
        stroke="var(--line)"
      />
      <text
        x="6"
        y="24"
        fontSize="20"
        fontFamily="'Source Serif 4', Georgia, serif"
        fontWeight={700}
        fill="var(--ink)"
      >
        B
      </text>
      <text
        x="17"
        y="24"
        fontSize="20"
        fontFamily="'Source Serif 4', Georgia, serif"
        fontWeight={400}
        fontStyle="italic"
        fill="var(--accent)"
      >
        h
      </text>
    </svg>
  );
}

export default function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Вход — bonds helper";
    return () => {
      document.title = prev;
    };
  }, []);

  if (status === "authenticated") {
    return <Navigate to={redirectTo} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password, rememberMe);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message =
        err instanceof LoginError
          ? err.message
          : "не удалось войти, попробуйте ещё раз";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--paper)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
      }}
    >
      <form
        onSubmit={onSubmit}
        className="card"
        style={{
          width: 380,
          padding: "36px 32px",
          boxShadow: "0 16px 40px rgba(20,20,30,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 28,
          }}
        >
          <HeroLogo size={48} />
          <div
            style={{
              fontSize: 18,
              marginTop: 10,
              fontFamily: "'Source Serif 4', Georgia, serif",
            }}
          >
            bonds <i style={{ color: "var(--accent)" }}>helper</i>
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4 }}>
            анализ и учёт облигаций
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="label" style={{ marginBottom: 6 }}>
            email
          </div>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="input"
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: 14,
              border: "1px solid var(--line)",
              borderRadius: 5,
            }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <div className="label" style={{ marginBottom: 6 }}>
              пароль
            </div>
            <span
              style={{ fontSize: 11.5, color: "var(--ink-soft)" }}
              title="восстановление пароля пока не реализовано"
            >
              забыли?
            </span>
          </div>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="input"
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: 14,
              border: "1px solid var(--line)",
              borderRadius: 5,
            }}
          />
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--ink-2)",
            marginBottom: 18,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          запомнить на 30 дней
        </label>

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12.5,
              color: "var(--bad)",
              background: "#fbf3f3",
              border: "1px solid var(--bad)",
              padding: "8px 10px",
              borderRadius: 4,
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn primary"
          disabled={submitting}
          style={{
            width: "100%",
            padding: "11px",
            fontSize: 14,
            opacity: submitting ? 0.7 : 1,
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting ? "входим…" : "Войти"}
        </button>
      </form>
    </div>
  );
}
